import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSriLankaDateString } from '../utils/dateUtils';
import { routeApi, scheduleApi, bookingApi } from '../services/api';
import { Bus, Calendar, Clock, Download, Loader2, AlertCircle, MapPin } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';

const DriverDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [error, setError] = useState('');
  const todayDate = getSriLankaDateString();

  // Check if user is logged in and has correct role
  useEffect(() => {
    if (!user || user.role !== 3) {
      navigate('/');
    }
  }, [user, navigate]);

  // Load routes on mount
  useEffect(() => {
    loadRoutes();
  }, []);

  // Load schedules when route is selected
  useEffect(() => {
    if (selectedRouteId) {
      loadTodaySchedules();
    } else {
      setSchedules([]);
    }
  }, [selectedRouteId]);

  const loadRoutes = async () => {
    try {
      setLoadingRoutes(true);
      const response = await routeApi.getRoutes();
      
      let routesArray = [];
      if (response?.value && Array.isArray(response.value)) {
        routesArray = response.value;
      } else if (Array.isArray(response)) {
        routesArray = response;
      }

      setRoutes(routesArray);
    } catch (err) {
      console.error('Error loading routes:', err);
      setError('Failed to load routes');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const loadTodaySchedules = async () => {
    if (!selectedRouteId || !user?.username) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get all schedules
      const response = await scheduleApi.getSchedules();
      
      let schedulesArray = [];
      if (response?.value && Array.isArray(response.value)) {
        schedulesArray = response.value;
      } else if (Array.isArray(response)) {
        schedulesArray = response;
      }

      // Filter schedules by:
      // 1. Route ID matches selected route
      // 2. Vehicle registration number ends with username (bus number)
      // 3. Has schedule date for today
      const busNumber = user.username; // Last 4 digits
      const targetDate = new Date(todayDate + 'T00:00:00.000Z');

      const filteredSchedules = schedulesArray
        .filter(schedule => {
          // Check route matches
          const routeMatch = (schedule.routeID || schedule.routeId || schedule.route?.routeID || schedule.route?.id) == selectedRouteId;
          
          // Check vehicle registration number ends with bus number
          const vehicleReg = schedule.vehicle?.registrationNumber || schedule.registrationNumber || '';
          const vehicleMatch = vehicleReg.endsWith(busNumber);
          
          // Check has schedule date for today
          const hasTodayDate = schedule.scheduleDates && 
            schedule.scheduleDates.length > 0 &&
            schedule.scheduleDates.some(sd => {
              const scheduleDate = new Date(sd.date);
              return scheduleDate.toDateString() === targetDate.toDateString();
            });

          return routeMatch && vehicleMatch && hasTodayDate;
        })
        .map(schedule => {
          // Find the matching scheduleDate for today
          const matchingScheduleDate = schedule.scheduleDates?.find(sd => {
            const scheduleDate = new Date(sd.date);
            return scheduleDate.toDateString() === targetDate.toDateString();
          });

          return {
            ...schedule,
            matchingScheduleDate: matchingScheduleDate || schedule.scheduleDates?.[0]
          };
        });

      setSchedules(filteredSchedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError('Failed to load schedules');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadSeatAvailabilityPDF = async (schedule) => {
    if (!schedule.matchingScheduleDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Schedule date information is missing.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we prepare your report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const scheduleDateId = schedule.matchingScheduleDate.scheduleDateID || schedule.matchingScheduleDate.scheduleDateId;
      const scheduleDate = schedule.matchingScheduleDate.date;
      
      // Get bookings for today's date
      const bookingsResponse = await bookingApi.getAllBookings({
        fromDate: todayDate,
        toDate: todayDate
      });

      let bookings = [];
      if (bookingsResponse?.value && Array.isArray(bookingsResponse.value)) {
        bookings = bookingsResponse.value;
      } else if (Array.isArray(bookingsResponse)) {
        bookings = bookingsResponse;
      }

      // Filter bookings by schedule date ID and non-cancelled status
      const activeBookings = bookings.filter(b => {
        const bookingScheduleDateId = b.scheduleDateID || b.scheduleDateId || b.scheduleDate?.scheduleDateID || b.scheduleDate?.scheduleDateId;
        return bookingScheduleDateId == scheduleDateId && 
               b.status !== 'Cancelled' && 
               b.paymentStatus !== 'Cancelled';
      });

      // Get booking seats
      const bookedSeatsData = [];
      for (const booking of activeBookings) {
        const bookingId = booking.bookingID || booking.bookingId || booking.id;
        if (bookingId) {
          try {
            const bookingSeatsResponse = await bookingApi.getBookingSeats(bookingId);
            let bookingSeats = [];
            if (bookingSeatsResponse?.value && Array.isArray(bookingSeatsResponse.value)) {
              bookingSeats = bookingSeatsResponse.value;
            } else if (Array.isArray(bookingSeatsResponse)) {
              bookingSeats = bookingSeatsResponse;
            }

            bookingSeats.forEach(bs => {
              bookedSeatsData.push({
                seatNumber: bs.seatNumber || bs.seat?.seatNumber || 'N/A',
                bookingID: bookingId,
                customerName: booking.customer?.customerName || booking.customerName || 'N/A',
                phone: booking.customer?.phone || booking.phone || 'N/A',
                price: booking.ticketPrice || booking.price || 0
              });
            });
          } catch (err) {
            console.error(`Error fetching seats for booking ${bookingId}:`, err);
          }
        }
      }

      // Create PDF
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Get route info
      const route = schedule.route || {};
      const fromLocation = route.fromLocation || route.fromCityName || 'N/A';
      const toLocation = route.toLocation || route.toCityName || 'N/A';
      const routeName = `${fromLocation} -> ${toLocation}`;
      
      // Get schedule info
      const departureTime = schedule.matchingScheduleDate.departureTime || schedule.departureTime || 'N/A';
      const arrivalTime = schedule.matchingScheduleDate.arrivalTime || schedule.arrivalTime || 'N/A';
      const vehicle = schedule.vehicle?.registrationNumber || 'N/A';
      const date = new Date(schedule.matchingScheduleDate.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Header
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Seat Availability Report', 14, 15);
      
      doc.setFontSize(10);
      doc.text(`Route: ${routeName}`, 14, 22);
      doc.text(`Date: ${date}`, 14, 27);
      doc.text(`Departure: ${departureTime}`, 14, 32);
      doc.text(`Arrival: ${arrivalTime}`, 14, 37);
      doc.text(`Vehicle: ${vehicle}`, 14, 42);

      // Table data
      const tableData = bookedSeatsData.map(seat => [
        seat.seatNumber,
        seat.customerName,
        seat.phone,
        `Rs ${seat.price.toFixed(2)}`
      ]);

      // Add table
      doc.autoTable({
        startY: 48,
        head: [['Seat Number', 'Customer Name', 'Phone', 'Price']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        styles: { fontSize: 8 },
        margin: { top: 48 }
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const fileName = `Seat_Availability_${routeName.replace(/\s+/g, '_')}_${date.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);

      Swal.close();
      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded',
        text: 'Seat availability report has been downloaded successfully.',
        confirmButtonColor: '#2563eb'
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/passenger/login');
  };

  if (!user || user.role !== 3) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Driver Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, Bus {user.username}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Route Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <MapPin className="w-4 h-4 inline mr-1" />
            Select Route
          </label>
          <select
            value={selectedRouteId}
            onChange={(e) => setSelectedRouteId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loadingRoutes}
          >
            <option value="">Select a route</option>
            {routes.map((route) => {
              const routeId = route.routeID || route.id;
              const fromLocation = route.fromLocation || route.fromCityName || 'N/A';
              const toLocation = route.toLocation || route.toCityName || 'N/A';
              return (
                <option key={routeId} value={routeId}>
                  {`${fromLocation} -> ${toLocation}`}
                </option>
              );
            })}
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Today's Date Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center">
          <Calendar className="w-5 h-5 text-blue-600 mr-2" />
          <span className="text-blue-800 font-medium">
            Today's Date: {new Date(todayDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>

        {/* Schedules List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading schedules...</span>
          </div>
        ) : schedules.length === 0 && selectedRouteId ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Bus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No schedules found for today</p>
          </div>
        ) : schedules.length > 0 ? (
          <div className="space-y-4">
            {schedules.map((schedule) => {
              const scheduleId = schedule.scheduleID || schedule.id;
              const route = schedule.route || {};
              const fromLocation = route.fromLocation || route.fromCityName || 'N/A';
              const toLocation = route.toLocation || route.toCityName || 'N/A';
              const routeName = `${fromLocation} -> ${toLocation}`;
              const departureTime = schedule.matchingScheduleDate?.departureTime || schedule.departureTime || 'N/A';
              const arrivalTime = schedule.matchingScheduleDate?.arrivalTime || schedule.arrivalTime || 'N/A';
              const vehicle = schedule.vehicle?.registrationNumber || 'N/A';

              return (
                <div key={scheduleId} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{routeName}</h3>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          <span>Departure: {departureTime}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          <span>Arrival: {arrivalTime}</span>
                        </div>
                        <div className="flex items-center">
                          <Bus className="w-4 h-4 mr-2" />
                          <span>Vehicle: {vehicle}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadSeatAvailabilityPDF(schedule)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Please select a route to view today's schedules</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
